// Full donor profile, mirroring the legacy AngularJS donor screen: identity, contacts,
// addresses, birthdays & anniversaries, recurring plans, donations (year summary + detail),
// gifts received, notes and tags. Donation figures follow the session's scope
// (LE + location + department, and the ?cause= filter); identity data is the donor's own.

import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Building2,
  CalendarClock,
  Cake,
  Clock,
  Gift,
  HandHeart,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Briefcase,
  Repeat,
  StickyNote,
  Tag,
  User,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fetchDonor, formatAmount, type DonorProfile } from "@/lib/donation";
import { formatDate } from "@/lib/format";
import { useSessionScope } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { PageContainer } from "@/components/page";
import { BlockPlaceholder, Busy } from "@/components/busy";
import { emailAddress, telNumber, whatsappNumber } from "@/lib/contact";
import { DonorLink } from "@/components/donor-link";
import { EmailValue, PhoneValue } from "@/components/contact-value";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export default function Donor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { token } = useAuth();
  const { scope } = useSessionScope();
  const donorId = Number(id);
  const causeId = Number(params.get("cause")) || null;
  const budgetYearId = Number(params.get("fy")) || null;

  const [data, setData] = useState<DonorProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !scope || !donorId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDonor(token, scope, donorId, { budgetYearId, causeId }, controller.signal)
      .then(setData)
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setError(
          e instanceof ApiError && e.status === 403
            ? 'Donor details need the "View donation" permission.'
            : (e as Error).message
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, scope, donorId, budgetYearId, causeId]);

  if (error) {
    return (
      <PageContainer full>
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">{error}</CardContent>
        </Card>
      </PageContainer>
    );
  }
  if (!data) {
    return (
      <PageContainer full>
        <BlockPlaceholder className="h-44" />
        <BlockPlaceholder className="h-64" />
      </PageContainer>
    );
  }

  const {
    donor,
    contacts,
    addresses,
    anniversaries,
    relationships,
    organizations,
    orgContacts,
    recurringPlans,
    yearSummary,
    donations,
    gifts,
    donorNotes,
    tags,
  } = data;
  const tel = telNumber(donor.mobile);
  const wa = whatsappNumber(donor.whatsAppNumber ?? donor.mobile);
  const mail = emailAddress(donor.email);
  const p = donor.privacy;

  return (
    <PageContainer full>
      <Busy busy={loading} className="flex flex-col gap-6">
        {/* --------------------------------------------------- header (stays put) */}
        <Card className="sticky top-0 z-20 shadow-sm">
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {donor.isOrganization ? (
                    <Building2 className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <User className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <h2 className="truncate text-lg font-semibold">{donor.name}</h2>
                  {donor.type && <Badge variant="outline">{donor.type}</Badge>}
                  {donor.isDeceased && <Badge variant="outline">Deceased</Badge>}
                  <span className="text-xs text-muted-foreground">#{donor.id}</span>
                </div>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                  {donor.mobile || donor.email ? (
                    <>
                      {donor.mobile && <PhoneValue value={donor.mobile} />}
                      {donor.mobile && donor.email && <span>·</span>}
                      {donor.email && <EmailValue value={donor.email} />}
                    </>
                  ) : (
                    "No contact details on record"
                  )}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.donNotCall && <Badge variant="outline" className="text-amber-700 dark:text-amber-400">Do not call</Badge>}
                  {p.doNotEmail && <Badge variant="outline" className="text-amber-700 dark:text-amber-400">Do not email</Badge>}
                  {p.doNotSms && <Badge variant="outline" className="text-amber-700 dark:text-amber-400">Do not SMS</Badge>}
                  {p.noBirthdayWish && <Badge variant="outline">No birthday wishes</Badge>}
                  {p.noAnniversaryWish && <Badge variant="outline">No anniversary wishes</Badge>}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {tel && (
                  <Button nativeButton={false} variant="outline" size="sm" render={<a href={`tel:${tel}`} />}>
                    <Phone /> Call
                  </Button>
                )}
                {wa && (
                  <Button
                    nativeButton={false}
                    variant="outline"
                    size="sm"
                    render={<a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" />}
                  >
                    <MessageCircle /> WhatsApp
                  </Button>
                )}
                {mail && (
                  <Button nativeButton={false} variant="outline" size="sm" render={<a href={`mailto:${mail}`} />}>
                    <Mail /> Email
                  </Button>
                )}
              </div>
            </div>

          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat
                icon={HandHeart}
                label={`FY ${data.budgetYear.name}`}
                value={formatAmount(data.totals.fy.amount)}
                sub={`${data.totals.fy.count} donations`}
              />
              <Stat
                icon={HandHeart}
                label="All years here"
                value={formatAmount(data.totals.lifetime.amount)}
                sub={`${data.totals.lifetime.count} donations`}
              />
              <Stat
                icon={CalendarClock}
                label="First / last"
                value={formatDate(data.totals.firstDonation)}
                sub={data.totals.lastDonation ? `last ${formatDate(data.totals.lastDonation)}` : ""}
              />
              <Stat
                icon={Clock}
                label="Pending receipt"
                value={formatAmount(data.totals.pending.amount)}
                sub={`${data.totals.pending.count} not yet received`}
              />
            </div>

            {tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Tag className="size-3.5 text-muted-foreground" />
                {tags.map((t) => (
                  <Badge key={t} variant="outline">
                    {t}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>


        {/* ---------------------------------------------------------- identity */}
        <Section title="Details" icon={User}>
          <Facts
            rows={[
              ["Display name", donor.displayName],
              ["Legal name", donor.legalName],
              ["Initiated name", donor.initiatedName],
              ["Spiritual master", donor.spiritualMaster],
              ["Name on receipt", donor.receiptName],
              ["Date of birth", donor.dob ? `${formatDate(donor.dob)}${donor.dobAuthenticity ? ` (${donor.dobAuthenticity})` : ""}` : null],
              ["Gender", donor.gender],
              ["Marital status", donor.maritalStatus],
              ["Type", donor.type],
              ["Center", donor.center],
              ["Satsanga", donor.satsangas],
              ["Referred by", donor.referredBy],
              ["PAN", donor.pan ? `${donor.pan}${donor.panVerified ? " (verified)" : ""}` : null],
              ["GSTN", donor.gstn],
              ["WhatsApp number", donor.whatsAppNumber ? <PhoneValue value={donor.whatsAppNumber} /> : null],
            ]}
          />
        </Section>

        {/* ---------------------------------------------------------- contacts */}
        {(contacts.mobiles.length > 0 || contacts.emails.length > 0 || contacts.phones.length > 0) && (
          <Section title="Contacts" icon={Phone}>
            <div className="grid gap-4 md:grid-cols-3">
              <ContactList
                heading="Mobiles"
                items={contacts.mobiles.map((m) => ({
                  node: <PhoneValue value={m.number} />,
                  meta: m.location,
                  primary: m.isPrimary,
                }))}
              />
              <ContactList
                heading="Emails"
                items={contacts.emails.map((e) => ({
                  node: <EmailValue value={e.email} />,
                  meta: [e.location, e.verified ? "verified" : null].filter(Boolean).join(" · "),
                  primary: e.isPrimary,
                }))}
              />
              <ContactList
                heading="Landlines"
                items={contacts.phones.map((ph) => ({
                  node: <PhoneValue value={ph.number} />,
                  meta: ph.location,
                  primary: ph.isPrimary,
                }))}
              />
            </div>
          </Section>
        )}

        {/* ---------------------------------------------------------- addresses */}
        {addresses.length > 0 && (
          <Section title="Addresses" icon={MapPin}>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {addresses.map((a) => (
                <div key={a.id} className="rounded-xl border border-border p-3 text-sm">
                  <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                    {a.location ?? "Address"}
                    {a.isPrimary && <Badge variant="outline" className="text-[10px]">Primary</Badge>}
                  </div>
                  <div>{a.lines || "—"}</div>
                  <div className="text-muted-foreground">
                    {[a.city, a.state, a.zip, a.country].filter(Boolean).join(", ")}
                  </div>
                  {(a.phone || a.email) && (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                      {a.phone && <PhoneValue value={a.phone} />}
                      {a.email && <EmailValue value={a.email} />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ------------------------------------------- birthdays & anniversaries */}
        {anniversaries.length > 0 && (
          <Section title="Birthdays and anniversaries" icon={Cake}>
            <Table
              head={["Date", "Type", "Authenticity", "Remarks"]}
              rows={anniversaries.map((a) => [formatDate(a.date), a.type, a.authenticity, a.remarks])}
            />
          </Section>
        )}

        {/* ------------------------------------------------------ relationships */}
        {relationships.length > 0 && (
          <Section title="Relationships" icon={Users}>
            <Table
              head={["Relation", "Name", "Gender", "Date of birth", "Mobile", "Email"]}
              rows={relationships.map((r) => [
                r.relationship,
                // Related people are donors in their own right only sometimes — the menu
                // decides what it can offer from the id / contacts it gets.
                <DonorLink
                  name={r.name ?? "—"}
                  donorId={r.id}
                />,
                r.gender,
                formatDate(r.dob),
                <PhoneValue value={r.mobile} />,
                <EmailValue value={r.email} />,
              ])}
            />
          </Section>
        )}

        {/* ------------------------------------------------------ organizations */}
        {organizations.length > 0 && (
          <Section title="Organizations" icon={Briefcase} description="Organisations this donor is a contact of">
            <Table
              head={["Organisation", "Designation", "Mobile", "Email", "Remarks"]}
              rows={organizations.map((o) => [
                <DonorLink name={o.name ?? "—"} donorId={o.donorId} />,
                o.designation,
                <PhoneValue value={o.mobile} />,
                <EmailValue value={o.email} />,
                o.remarks,
              ])}
            />
          </Section>
        )}

        {orgContacts.length > 0 && (
          <Section title="Contacts" icon={Users} description="People who are contacts of this organisation">
            <Table
              head={["Name", "Designation", "Mobile", "Email", "Remarks"]}
              rows={orgContacts.map((c) => [
                <DonorLink name={c.name ?? "—"} donorId={c.donorId} />,
                c.designation,
                <PhoneValue value={c.mobile} />,
                <EmailValue value={c.email} />,
                c.remarks,
              ])}
            />
          </Section>
        )}

        {/* ---------------------------------------------------------- recurring */}
        {recurringPlans.length > 0 && (
          <Section title="Recurring donation profiles" icon={Repeat}>
            <Table
              head={["Cause", "Location", "Amount", "Starts", "Ends", "Repeats", "Active"]}
              align={{ 2: "right" }}
              rows={recurringPlans.map((r) => [
                r.cause,
                r.location,
                formatAmount(r.amount),
                formatDate(r.startDate),
                r.endDate ? formatDate(r.endDate) : "Never ends",
                r.repeats,
                r.active ? "Yes" : "No",
              ])}
            />
          </Section>
        )}

        {/* ---------------------------------------------------------- donations */}
        {yearSummary.length > 0 && (
          <Section title="Donations by year" icon={HandHeart} description="Received, in this scope">
            <Table
              head={["Financial year", "Donations", "Amount"]}
              align={{ 1: "right", 2: "right" }}
              rows={yearSummary.map((y) => [y.name, String(y.count), formatAmount(y.amount)])}
              footer={[
                "Total",
                String(yearSummary.reduce((s, y) => s + y.count, 0)),
                formatAmount(yearSummary.reduce((s, y) => s + y.amount, 0)),
              ]}
            />
          </Section>
        )}

        <Section
          title="Donations"
          icon={HandHeart}
          description={`In ${scope?.operationUnit.name ?? ""}${scope?.department ? ` · ${scope.department.name}` : ""} · newest first${
            donations.length >= 200 ? " · latest 200" : ""
          }`}
        >
          {donations.length === 0 ? (
            <Empty>No donations in this scope.</Empty>
          ) : (
            <Table
              head={["Receipt", "Date", "Causes", "Mode", "Amount"]}
              align={{ 4: "right" }}
              rows={donations.map((d) => [
                <>
                  {d.receiptNumber ?? "—"}
                  {!d.received && (
                    <Badge variant="outline" className="ml-2 gap-1 text-[10px]">
                      <Clock className="size-3" /> Pending
                    </Badge>
                  )}
                </>,
                formatDate(d.date),
                <>
                  {d.causes ?? "—"}
                  {d.softCredits.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Soft credit:{" "}
                      {d.softCredits
                        .map((sc) =>
                          [sc.name, sc.type ? `(${sc.type})` : null, sc.amount ? formatAmount(sc.amount) : null]
                            .filter(Boolean)
                            .join(" ")
                        )
                        .join(", ")}
                    </div>
                  )}
                </>,
                d.modeOfPayment,
                formatAmount(d.amount),
              ])}
            />
          )}
        </Section>

        {/* ---------------------------------------------------------- gifts */}
        {gifts.length > 0 && (
          <Section title="Gifts given" icon={Gift} description="Items given to the donor in reciprocation">
            <Table
              head={["Gift", "Delivered", "Method", "Value", "For receipt", "Year"]}
              align={{ 3: "right" }}
              rows={gifts.map((g) => [
                g.gift ?? g.description,
                formatDate(g.deliveryDate),
                g.deliveryMethod,
                formatAmount(g.value),
                g.receiptNumber,
                g.budgetYear,
              ])}
            />
          </Section>
        )}

        {/* ---------------------------------------------------------- notes */}
        {donorNotes.length > 0 && (
          <Section title="Notes" icon={StickyNote}>
            <ul className="divide-y divide-border">
              {donorNotes.map((n) => (
                <li key={n.id} className="py-2 text-sm">
                  <div className="whitespace-pre-wrap">{n.note}</div>
                  <div className="text-xs text-muted-foreground">
                    {[formatDate(n.date), n.by].filter(Boolean).join(" · ")}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </Busy>
    </PageContainer>
  );
}

// ------------------------------------------------------------------ building blocks

function Section({
  title,
  icon: Icon,
  description,
  children,
}: {
  title: string;
  icon: LucideIcon;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="size-4 text-muted-foreground" /> {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/**
 * Label / value facts. On a phone: one field per line, label left and value right (the usual
 * mobile detail layout, same as the donor sheet). From md up: two columns, values left-aligned
 * under each other so a wide screen stays scannable.
 */
function Facts({ rows }: { rows: [string, React.ReactNode][] }) {
  const shown = rows.filter(([, v]) => v != null && v !== "");
  if (shown.length === 0) return <Empty>Nothing recorded.</Empty>;
  return (
    <div className="grid gap-x-6 md:grid-cols-2">
      {shown.map(([label, value]) => (
        <div
          key={label}
          className="flex items-baseline justify-between gap-4 border-b border-border/60 py-1.5 text-sm last:border-0 md:justify-start md:gap-3"
        >
          <span className="shrink-0 text-muted-foreground md:w-2/5">{label}</span>
          <span className="min-w-0 text-right break-words md:flex-1 md:text-left">{value}</span>
        </div>
      ))}
    </div>
  );
}

function ContactList({
  heading,
  items,
}: {
  heading: string;
  items: { node: React.ReactNode; meta?: string | null; primary: boolean }[];
}) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">{heading}</div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">—</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {items.map((i, n) => (
            <li key={n} className="flex flex-wrap items-center gap-1.5">
              {i.node}
              {i.primary && <Badge variant="outline" className="text-[10px]">Primary</Badge>}
              {i.meta && <span className="text-xs text-muted-foreground">{i.meta}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Table({
  head,
  rows,
  align = {},
  footer,
}: {
  head: string[];
  rows: (React.ReactNode | string | null)[][];
  align?: Record<number, "right">;
  footer?: (string | null)[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            {head.map((h, i) => (
              <th key={h} className={cn("py-2 pr-3 font-medium whitespace-nowrap", align[i] === "right" && "text-right")}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-b border-border/60 last:border-0">
              {r.map((c, ci) => (
                <td
                  key={ci}
                  className={cn(
                    "py-1.5 pr-3 align-top",
                    align[ci] === "right" && "text-right tabular-nums whitespace-nowrap"
                  )}
                >
                  {c == null || c === "" ? <span className="text-muted-foreground">—</span> : c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="font-medium">
              {footer.map((c, i) => (
                <td key={i} className={cn("py-2 pr-3", align[i] === "right" && "text-right tabular-nums")}>
                  {c}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-4 text-center text-sm text-muted-foreground">{children}</p>;
}

function Stat({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="size-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="truncate text-base font-semibold tabular-nums">{value}</div>
      {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
